import { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faSpinner, 
  faClock, 
  faCheckCircle, 
  faExclamationCircle,
  faPause,
  faPlay,
  faTimesCircle,
  faGripVertical,
  faHistory,
  faSort,
  faSortUp,
  faSortDown
} from '@fortawesome/free-solid-svg-icons';
import RelatoriosAgendadosService from '../../../services/relatoriosAgendadosService';
import CustomAlert from '../../../shared/customAlert';
import Spinner from '../../../shared/spinner';
import { parseBackendLocalDateTime } from '../../../shared/agendamentoRelatorioData';

interface HistoricoItem {
  id: number;
  idRelatorio: string;
  datetime: string;
  descricao: string;
}

interface RelatorioAgendado {
  id: string;
  userId: string;
  usuario: string;
  tipoRelatorio: string;
  parametros: string;
  historico?: HistoricoItem[];
  qtdeLinhas: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PAUSED' | 'CANCELLED';
  dtaCriacao: string;
  programado: string;
  tmpInicio?: string;
  tmpFinal?: string;
  pastaArquivo?: string;
  canDownload: boolean;
  canPause: boolean;
  canResume: boolean;
  canCancel: boolean;
  posicaoFila?: number;
  ordem?: number;
}

interface ProgressoRelatorio {
  id: string;
  status: string;
  tipoRelatorio: string;
  dataSolicitacao: string;
  progressoPorcentagem?: number;
  linhasProcessadas?: number;
  totalLinhas?: number;
  tempoDecorrido?: string;
  tempoEstimadoRestante?: string;
  tempoFormatado?: string;
  inicioProcessamento?: string;
  fimProcessamento?: string;
  duracaoTotal?: string;
  pausado?: boolean;
}

interface DownloadCount {
  [relatorioId: string]: number;
}

interface Ordenacao {
  campo: 'posicaoFila' | 'tipoRelatorio' | 'status' | 'dtaCriacao' | 'programado';
  direcao: 'asc' | 'desc';
}

function RelatoriosAgendadosComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [relatorios, setRelatorios] = useState<RelatorioAgendado[]>([]);
  const [filtroStatus, setFiltroStatus] = useState<'TODOS' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PAUSED'>('TODOS');
  const [progressos, setProgressos] = useState<Record<string, ProgressoRelatorio>>({});
  const [modalParametrosAberto, setModalParametrosAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState<RelatorioAgendado | null>(null);
  const intervalRef = useRef<number>();
  const [monitoramentoAtivo, setMonitoramentoAtivo] = useState<Record<string, number>>({});
  
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [tamanhoPagina] = useState(10);

  const [draggedItem, setDraggedItem] = useState<RelatorioAgendado | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [downloadCounts, setDownloadCounts] = useState<DownloadCount>({});
  const [ordenacao, setOrdenacao] = useState<Ordenacao>({
    campo: 'posicaoFila',
    direcao: 'asc'
  });

  const relatoriosAgendadosService = new RelatoriosAgendadosService();
  const userId = localStorage.getItem("id") ?? "";
  const usuario = localStorage.getItem("nome") ?? "";

  // Função para obter a role do usuário
  const getUserRole = (): string => {
    return localStorage.getItem("role") || "";
  };

  useEffect(() => {
    const savedDownloadCounts = localStorage.getItem(`downloadCounts_${userId}`);
    if (savedDownloadCounts) {
      setDownloadCounts(JSON.parse(savedDownloadCounts));
    }
  }, [userId]);

  useEffect(() => {
    localStorage.setItem(`downloadCounts_${userId}`, JSON.stringify(downloadCounts));
  }, [downloadCounts, userId]);

  useEffect(() => {
    carregarRelatorios();
  }, [paginaAtual, ordenacao]);

  const formatarTempoEstimado = (segundos: number) => {
    if (!segundos) return 'Calculando...';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${horas > 0 ? horas + 'h ' : ''}${minutos > 0 ? minutos + 'm ' : ''}${segs}s`;
  };

  const traduzirStatus = (status: string): string => {
    switch (status) {
      case 'PENDING': return 'Pendente';
      case 'PROCESSING': return 'Processando';
      case 'COMPLETED': return 'Concluído';
      case 'FAILED': return 'Falhou';
      case 'PAUSED': return 'Pausado';
      case 'CANCELLED': return 'Cancelado';
      default: return status;
    }
  };

  const formatarData = (dataString?: string) => {
    if (!dataString) return '-';
    const data = parseBackendLocalDateTime(dataString);
    if (!data) return '-';
    return data.toLocaleString('pt-BR');
  };

  const formatarHora = (dataString: string) => {
    if (!dataString) return '-';
    const data = parseBackendLocalDateTime(dataString);
    if (!data) return '-';
    return data.toLocaleTimeString('pt-BR');
  };

  const calcularDuracaoSegundos = (inicio?: string, fim?: string) => {
    const dataInicio = parseBackendLocalDateTime(inicio);
    const dataFim = parseBackendLocalDateTime(fim);
    if (!dataInicio || !dataFim) return null;
    return Math.round((dataFim.getTime() - dataInicio.getTime()) / 1000);
  };

  const formatarDuracaoRelatorio = (inicio?: string, fim?: string) => {
    const duracao = calcularDuracaoSegundos(inicio, fim);
    return duracao !== null ? `${duracao}s` : '-';
  };

  const gerarNomeArquivo = (relatorio: RelatorioAgendado): string => {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');
    const dataCompleta = `${ano}-${mes}-${dia}`;
    
    const qtdeAtual = downloadCounts[relatorio.id] || 0;
    const novaQtde = qtdeAtual + 1;
    
    setDownloadCounts(prev => ({
      ...prev,
      [relatorio.id]: novaQtde
    }));
    
    const nomeRelatorio = relatorio.tipoRelatorio
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    
    return `${dataCompleta}-${novaQtde.toString().padStart(3, '0')}-${nomeRelatorio}.csv`;
  };

  const abrirModalParametros = (relatorio: RelatorioAgendado) => {
    setRelatorioSelecionado(relatorio);
    setModalParametrosAberto(true);
  };

  const abrirModalHistorico = (relatorio: RelatorioAgendado) => {
    setRelatorioSelecionado(relatorio);
    setModalHistoricoAberto(true);
  };

  const parseParametros = (parametros: string) => {
    try {
      return JSON.parse(parametros);
    } catch {
      return {};
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'PENDING': return 'bg-secondary';
      case 'PROCESSING': return 'bg-primary';
      case 'COMPLETED': return 'bg-success';
      case 'FAILED': return 'bg-danger';
      case 'PAUSED': return 'bg-warning';
      case 'CANCELLED': return 'bg-dark';
      default: return 'bg-info';
    }
  };

  const handleDragStart = (e: React.DragEvent, relatorio: RelatorioAgendado) => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    if (filtroStatus !== 'TODOS' || 
        relatorio.status !== 'PENDING' || 
        !allowedRoles.includes(userRole)) {
      e.preventDefault();
      return;
    }
    
    e.dataTransfer.setData('text/plain', relatorio.id);
    setDraggedItem(relatorio);
    setIsDragging(true);
    
    const target = e.currentTarget as HTMLElement;
    target.classList.add('dragging');
  };

  const handleDragOver = (e: React.DragEvent) => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    if (filtroStatus !== 'TODOS' || 
        !draggedItem || 
        !allowedRoles.includes(userRole)) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetRelatorio: RelatorioAgendado) => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    if (!allowedRoles.includes(userRole) ||
        filtroStatus !== 'TODOS' || 
        !draggedItem || 
        draggedItem.id === targetRelatorio.id || 
        targetRelatorio.status !== 'PENDING') {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    const newRelatorios = [...relatorios];
    const draggedIndex = newRelatorios.findIndex(r => r.id === draggedItem.id);
    const targetIndex = newRelatorios.findIndex(r => r.id === targetRelatorio.id);

    const [removed] = newRelatorios.splice(draggedIndex, 1);
    newRelatorios.splice(targetIndex, 0, removed);

    const relatoriosComOrdem = newRelatorios.map((rel, index) => ({
      ...rel,
      ordem: rel.status === 'PENDING' ? index + 1 : rel.ordem
    }));

    setRelatorios(relatoriosComOrdem);

    try {
      await relatoriosAgendadosService.moverNaFila({
        idOrigem: draggedItem.id,
        idDestino: targetRelatorio.id,
        userId: Number(userId),
        usuario: usuario
      });
      setAlert({ message: 'Relatórios reordenados com sucesso', type: 'success' });
      carregarRelatorios();
    } catch (error) {
      console.error('Erro ao mover relatório:', error);
      setAlert({ message: 'Erro ao mover relatório na fila', type: 'error' });
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    setDraggedItem(null);
    
    const elements = document.querySelectorAll('.draggable-item');
    elements.forEach(el => el.classList.remove('dragging', 'drag-over'));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    if (filtroStatus !== 'TODOS' || !allowedRoles.includes(userRole)) return;
    
    const target = e.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  };

  const podeArrastar = (relatorio: RelatorioAgendado): boolean => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    return filtroStatus === 'TODOS' && 
           relatorio.status === 'PENDING' && 
           allowedRoles.includes(userRole);
  };

  const getDragHandleTitle = (): string => {
    const userRole = getUserRole();
    const allowedRoles = ['Master', 'Matriz'];
    
    if (!allowedRoles.includes(userRole)) {
      return "Apenas usuários Master ou Matriz podem reordenar a fila";
    }
    return "Arraste para reordenar";
  };

  const gerenciarMonitoramento = useCallback((id: string, status: string) => {
    if (monitoramentoAtivo[id]) {
      window.clearInterval(monitoramentoAtivo[id]);
      setMonitoramentoAtivo(prev => {
        const novo = { ...prev };
        delete novo[id];
        return novo;
      });
    }

    if (status === 'PENDING' || status === 'PROCESSING' || status === 'PAUSED') {
      const intervalId = window.setInterval(async () => {
        try {
          const response = await relatoriosAgendadosService.progresso(id);
          const dados: ProgressoRelatorio = response.data;
          
          setProgressos(prev => ({
            ...prev,
            [id]: {
              ...prev[id],
              ...dados,
              tempoFormatado: dados.tempoEstimadoRestante 
                ? formatarTempoEstimado(parseInt(dados.tempoEstimadoRestante))
                : 'Calculando...'
            }
          }));

          if (dados.status !== status) {
            setRelatorios(prev => 
              prev.map(rel => 
                rel.id === id ? { ...rel, status: dados.status as any } : rel
              )
            );
          }

          if (dados.status === 'COMPLETED' || dados.status === 'FAILED') {
            window.clearInterval(intervalId);
            setMonitoramentoAtivo(prev => {
              const novo = { ...prev };
              delete novo[id];
              return novo;
            });
            carregarRelatorios();
          }
        } catch (error) {
          console.error('Erro ao monitorar progresso:', error);
        }
      }, 10000);

      setMonitoramentoAtivo(prev => ({ ...prev, [id]: intervalId }));
    }
  }, [monitoramentoAtivo]);

  useEffect(() => {
    relatorios.forEach(rel => {
      if (rel.status === 'PENDING' || rel.status === 'PROCESSING' || rel.status === 'PAUSED') {
        if (!monitoramentoAtivo[rel.id]) {
          gerenciarMonitoramento(rel.id, rel.status);
        }
      }
    });
  }, [relatorios, gerenciarMonitoramento, monitoramentoAtivo]);

  useEffect(() => {
    return () => {
      Object.values(monitoramentoAtivo).forEach(intervalId => {
        window.clearInterval(intervalId);
      });
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [monitoramentoAtivo]);

  const carregarRelatorios = async () => {
    setLoading(true);
    try {
      const response = await relatoriosAgendadosService.listar(
        userId, 
        paginaAtual, 
        tamanhoPagina,
        ordenacao.campo,
        ordenacao.direcao
      );
      const { content, totalPages } = response.data;

      let ordemCounter = 1;
      const relatoriosComOrdem = content.map((rel: RelatorioAgendado) => ({
        ...rel,
        ordem: rel.status === 'PENDING' ? ordemCounter++ : undefined
      }));

      setRelatorios(relatoriosComOrdem);
      setTotalPaginas(totalPages);

      const novosProgressos: Record<string, ProgressoRelatorio> = {};
      content.forEach((rel: RelatorioAgendado) => {
        if (['PENDING', 'PROCESSING', 'PAUSED'].includes(rel.status)) {
          novosProgressos[rel.id] = {
            id: rel.id,
            status: rel.status,
            tipoRelatorio: rel.tipoRelatorio,
            dataSolicitacao: rel.dtaCriacao,
            progressoPorcentagem: 0,
            linhasProcessadas: 0,
            totalLinhas: rel.qtdeLinhas || 0
          };
        }
      });

      setProgressos(novosProgressos);
    } catch (err) {
      setAlert({ message: 'Erro ao carregar relatórios agendados', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const baixarRelatorio = async (id: string, relatorio: RelatorioAgendado) => {
    try {
      setLoading(true);
      
      const nomeArquivo = gerarNomeArquivo(relatorio);
      
      const response = await relatoriosAgendadosService.downloadRelatorio(id, {
        responseType: 'blob'
      }); 

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setAlert({ message: `Download iniciado com sucesso: ${nomeArquivo}`, type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao baixar o relatório', type: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const pausarRelatorio = async (id: string) => {
    try {
      await relatoriosAgendadosService.pausarProcessamento(id, userId);
      setRelatorios(prev =>
        prev.map(r => r.id === id ? { ...r, status: 'PAUSED' } : r)
      );
      setAlert({ message: 'Relatório pausado com sucesso', type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao pausar relatório', type: 'error' });
      console.error(err);
    }
  };

  const retomarRelatorio = async (id: string) => {
    try {
      await relatoriosAgendadosService.retomarProcessamento(id, userId);
      setRelatorios(prev =>
        prev.map(r => r.id === id ? { ...r, status: 'PROCESSING' } : r)
      );
      setAlert({ message: 'Relatório retomado com sucesso', type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao retomar relatório', type: 'error' });
      console.error(err);
    }
  };

  const cancelarRelatorio = async (id: string) => {
    try {
      await relatoriosAgendadosService.cancelarProcessamento(id, userId);
      setRelatorios(prev =>
        prev.map(r => r.id === id ? { ...r, status: 'CANCELLED' } : r)
      );
      setAlert({ message: 'Relatório cancelado com sucesso', type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao cancelar relatório', type: 'error' });
      console.error(err);
    }
  };

  const handleOrdenacao = (campo: Ordenacao['campo']) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getIconeOrdenacao = (campo: string) => {
    if (ordenacao.campo !== campo) {
      return <FontAwesomeIcon icon={faSort} className="ms-1" />;
    }
    return ordenacao.direcao === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
      : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
  };

  const relatoriosFiltrados = relatorios.filter(rel =>
    filtroStatus === 'TODOS' || rel.status === filtroStatus
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <FontAwesomeIcon icon={faClock} className="text-secondary" />;
      case 'PROCESSING':
        return <FontAwesomeIcon icon={faSpinner} spin className="text-primary" />;
      case 'COMPLETED':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />;
      case 'PAUSED':
        return <FontAwesomeIcon icon={faPause} className="text-warning" />;
      case 'CANCELLED':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-secondary" />;
      default:
        return null;
    }
  };

  const dragDropStyles = `
    .draggable-item {
      cursor: grab;
      transition: all 0.2s ease;
    }
    .draggable-item.dragging {
      opacity: 0.5;
      background-color: #f8f9fa;
    }
    .draggable-item.drag-over {
      background-color: #e3f2fd;
      border: 2px dashed #2196f3;
    }
    .drag-handle {
      cursor: grab;
      margin-right: 8px;
      color: #6c757d;
    }
    .drag-handle:active {
      cursor: grabbing;
    }
    .no-drag {
      cursor: not-allowed;
      opacity: 0.6;
    }
    .text-muted .fa-grip-vertical {
      opacity: 0.3;
      cursor: not-allowed;
    }
    .sortable-header {
      cursor: pointer;
      user-select: none;
    }
    .sortable-header:hover {
      background-color: #f8f9fa;
    }
  `;

  const historicoStyles = `
    .modal-xl {
      max-width: 1200px;
    }
    .history-table {
      font-size: 0.9rem;
    }
    .history-table th {
      position: sticky;
      top: 0;
      background-color: #0d6efd;
      color: white;
    }
    .btn-history {
      min-width: 80px;
    }
  `;

  return (
    <div className="container-fluid">
      <style>{dragDropStyles}{historicoStyles}</style>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-dark">Relatórios Agendados</h6>
          <div className="d-flex gap-2">
            <select
              className="form-control"
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value as any)}
            >
              <option value="TODOS">Todos</option>
              <option value="PENDING">Pendentes</option>
              <option value="PROCESSING">Em Progresso</option>
              <option value="PAUSED">Pausados</option>
              <option value="COMPLETED">Concluídos</option>
              <option value="FAILED">Falhas</option>
            </select>
          </div>
        </div>

        <div className="card-body">
          {/* Modais (mantidos iguais) */}
          {modalParametrosAberto && relatorioSelecionado && (
            <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Parâmetros do Relatório - {relatorioSelecionado.tipoRelatorio}</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setModalParametrosAberto(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <pre>{JSON.stringify(parseParametros(relatorioSelecionado.parametros), null, 2)}</pre>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setModalParametrosAberto(false)}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {modalHistoricoAberto && relatorioSelecionado && (
            <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header bg-dark text-white">
                    <h5 className="modal-title">
                      <FontAwesomeIcon icon={faHistory} className="me-2" />
                      Histórico do Relatório - {relatorioSelecionado.tipoRelatorio}
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="Close"
                      onClick={() => setModalHistoricoAberto(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <strong>ID do Relatório:</strong> {relatorioSelecionado.id}
                      </div>
                      <div>
                        <strong>Usuário:</strong> {relatorioSelecionado.usuario}
                      </div>
                    </div>
                    
                    {relatorioSelecionado.historico && relatorioSelecionado.historico.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm table-striped table-hover">
                          <thead className="table-primary">
                            <tr>
                              <th>Data/Hora</th>
                              <th>Descrição</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatorioSelecionado.historico
                              .sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime())
                              .map((item, index) => (
                              <tr key={item.id || index}>
                                <td style={{ width: '180px' }}>
                                  <div className="fw-bold">{formatarData(item.datetime)}</div>
                                  <small className="text-muted">{formatarHora(item.datetime)}</small>
                                </td>
                                <td>{item.descricao || 'Sem descrição'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <FontAwesomeIcon icon={faHistory} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">Nenhum registro de histórico</h5>
                        <p className="text-muted">Não há eventos registrados no histórico deste relatório.</p>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setModalHistoricoAberto(false)}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <Spinner loading={loading} />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered" width="100%" cellSpacing="0">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th className="sortable-header" onClick={() => handleOrdenacao('tipoRelatorio')}>
                      Tipo {getIconeOrdenacao('tipoRelatorio')}
                    </th>
                    <th>Usuário</th>
                    <th>Parâmetros</th>
                    <th>Histórico</th>
                    <th className="sortable-header" onClick={() => handleOrdenacao('status')}>
                      Status {getIconeOrdenacao('status')}
                    </th>
                    <th className="sortable-header" onClick={() => handleOrdenacao('posicaoFila')}>
                      Fila {getIconeOrdenacao('posicaoFila')}
                    </th>
                    <th>Progresso</th>
                    <th className="sortable-header" onClick={() => handleOrdenacao('dtaCriacao')}>
                      Solicitação {getIconeOrdenacao('dtaCriacao')}
                    </th>
                    <th className="sortable-header" onClick={() => handleOrdenacao('programado')}>
                      Programado {getIconeOrdenacao('programado')}
                    </th>
                    <th>Início</th>
                    <th>Término</th>
                    <th>Duração</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {relatoriosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="text-center">Nenhum relatório encontrado</td>
                    </tr>
                  ) : (
                    relatoriosFiltrados.map((relatorio) => {
                      const progresso = progressos[relatorio.id];
                      const emProcessamento = relatorio.status === 'PROCESSING';
                      const pausado = relatorio.status === 'PAUSED';
                      const concluido = relatorio.status === 'COMPLETED';
                      const podeArrastarItem = podeArrastar(relatorio);

                      return (
                        <tr 
                          key={relatorio.id}
                          className={podeArrastarItem ? 'draggable-item' : 'no-drag'}
                          draggable={podeArrastarItem}
                          onDragStart={(e) => handleDragStart(e, relatorio)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, relatorio)}
                          onDragEnd={handleDragEnd}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                        >
                          <td>
                            {podeArrastarItem && (
                              <span 
                                className="drag-handle"
                                title={getDragHandleTitle()}
                              >
                                <FontAwesomeIcon icon={faGripVertical} />
                              </span>
                            )}
                            {!podeArrastarItem && relatorio.status === 'PENDING' && (
                              <span 
                                className="text-muted"
                                title={getDragHandleTitle()}
                              >
                                <FontAwesomeIcon icon={faGripVertical} />
                              </span>
                            )}
                          </td>
                          <td>{relatorio.tipoRelatorio}</td>
                          <td>{relatorio.userId} - {relatorio.usuario}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => abrirModalParametros(relatorio)}
                            >
                              Ver parâmetros
                            </button>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-info btn-history"
                              onClick={() => abrirModalHistorico(relatorio)}
                              disabled={!relatorio.historico || relatorio.historico.length === 0}
                              title="Ver histórico"
                            >
                              <FontAwesomeIcon icon={faHistory} />
                              {relatorio.historico && relatorio.historico.length > 0 && (
                                <span className="badge bg-secondary ms-1">{relatorio.historico.length}</span>
                              )}
                            </button>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {getStatusIcon(relatorio.status)}
                              <span className="ms-2">
                                {traduzirStatus(relatorio.status)}
                              </span>
                            </div>
                          </td>
                          <td>
                            {(relatorio.status === 'PROCESSING' || relatorio.status === 'PENDING' || relatorio.status === 'PAUSED') && relatorio.posicaoFila ? (
                              <span className={`badge ${relatorio.status === 'PROCESSING' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
                                <b>{relatorio.posicaoFila}º</b> {relatorio.status === 'PROCESSING' ? '(EM PROCESSAMENTO)' : '(EM BREVE SERÁ PROCESSADO)'}
                              </span>
                            ) : (
                              <span className="badge bg-secondary">NÃO SE APLICA</span>
                            )}
                          </td>
                          <td>
                            {['PROCESSING', 'PAUSED'].includes(relatorio.status) && progresso ? (
                              <div className="progress-container">
                                <div className="d-flex justify-content-between">
                                  <span>{progresso.progressoPorcentagem}%</span>
                                  <span>{progresso.tempoFormatado || 'Calculando...'}</span>
                                </div>
                                <div className="progress">
                                  <div
                                    className={`progress-bar ${relatorio.status === 'PAUSED' ? 'bg-warning' : ''}`}
                                    role="progressbar"
                                    style={{ width: `${progresso.progressoPorcentagem}%` }}
                                    aria-valuenow={progresso.progressoPorcentagem}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                  />
                                </div>
                                <div className="small text-muted">
                                  {progresso.linhasProcessadas}/{progresso.totalLinhas} linhas processadas
                                </div>
                              </div>
                            ) : relatorio.status === 'COMPLETED' ? (
                              <span className="text-success">100% concluído</span>
                            ) : relatorio.status === 'FAILED' ? (
                              <span className="text-danger">Falhou - ver histórico</span>
                            ) : null}
                          </td>
                          <td>{formatarData(relatorio.dtaCriacao)}</td>
                          <td>{formatarData(relatorio.programado)}</td>
                          <td>{formatarData(relatorio.tmpInicio)}</td>
                          <td>{formatarData(relatorio.tmpFinal)}</td>
                          <td>
                            {progresso?.duracaoTotal || formatarDuracaoRelatorio(relatorio.tmpInicio, relatorio.tmpFinal)}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              {emProcessamento && relatorio.canPause && (
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => pausarRelatorio(relatorio.id)}
                                  title="Pausar"
                                >
                                  <FontAwesomeIcon icon={faPause} />
                                </button>
                              )}
                              {pausado && relatorio.canResume && (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => retomarRelatorio(relatorio.id)}
                                  title="Retomar"
                                >
                                  <FontAwesomeIcon icon={faPlay} />
                                </button>
                              )}
                              {concluido && relatorio.pastaArquivo && relatorio.canDownload && (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => baixarRelatorio(relatorio.id, relatorio)}
                                  title="Download"
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                </button>
                              )}
                              {['PROCESSING', 'PAUSED', 'PENDING'].includes(relatorio.status) && relatorio.canCancel && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => cancelarRelatorio(relatorio.id)}
                                  title="Cancelar"
                                >
                                  <FontAwesomeIcon icon={faTimesCircle} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-secondary"
                  disabled={paginaAtual === 0}
                  onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 0))}
                >
                  Página anterior
                </button>

                <span>Página {paginaAtual + 1} de {totalPaginas}</span>

                <button
                  className="btn btn-secondary"
                  disabled={paginaAtual >= totalPaginas - 1}
                  onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas - 1))}
                >
                  Próxima página
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RelatoriosAgendadosComponent;
